import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { supabaseClient } from 'src/auth/supabase';
import { buildBasketUser, buildUser } from 'src/utils/build-user';
import { PathNames } from 'src/shared/enum/PathNames';
import { useHandleQueryError } from 'src/lib/hooks/use-handle-query-error';
import { ApiBasket } from 'src/api/basketService';
import { Building } from 'src/shared/types/building';
import { useAddUserToBasket } from 'src/lib/hooks/use-post-add-user-to-basket';
import { usePatchUserMetaData } from 'src/lib/hooks/use-patch-user-meta-data';

import { UserDataFormValues } from '../../details-form/types';
import { routes } from '../../../constants/routes';

interface UseUserFormSubmitProps {
    userLoggedIn: boolean;
    userId: string | null;
    basket: ApiBasket;
    building: Building;
    hasRoommates: boolean;
}

export const useUserFormSubmit = ({
    userLoggedIn,
    userId,
    basket,
    building,
    hasRoommates,
}: UseUserFormSubmitProps) => {
    const navigate = useNavigate();
    const handleQueryError = useHandleQueryError();

    const { mutateAsync: addUserToBasket } = useAddUserToBasket();
    const { mutateAsync: updateUserMetaData } = usePatchUserMetaData();

    const mutation = useMutation({
        mutationFn: async (values: UserDataFormValues) => {
            const mapperOptions = {
                isSpanishTaxApplicable: building.spanish_tax_applicable,
            };

            const user = buildUser(values, mapperOptions);
            const basketUser = buildBasketUser(values, userId, mapperOptions);

            if (!userLoggedIn) {
                await supabaseClient.auth.signUp({
                    email: values.email,
                    password: values.password,
                });

                const response = await supabaseClient.auth.updateUser({
                    data: user,
                });

                if (response.error) {
                    return navigate(routes.failure.index);
                }

                const { user: updatedUser } = response.data;

                return addUserToBasket({
                    basketId: basket.id,
                    userData: { ...basketUser, id: updatedUser.id },
                    updatedAt: basket.updated_date,
                });
            }

            await updateUserMetaData({ metadata: user });

            return addUserToBasket({
                basketId: basket.id,
                userData: basketUser,
                updatedAt: basket.updated_date,
            });
        },

        onSuccess: () => {
            const nextPath = hasRoommates ? PathNames.ROOMMATES : PathNames.AGREEMENTS;

            navigate(nextPath);
        },

        onError: (error) => {
            handleQueryError(error as Error, basket.products[0].academic_year);
        },
    });

    return {
        submitHandler: mutation.mutate,
        loading: mutation.isPending,
    };
};
